import {
  buildPlatformScope,
  buildQuickDiagnosisReport,
  getQuestionTypeLabel,
  questionShowsAudioStimulus,
  type Question,
} from '@examflow/core';
import {
  buildAttemptHref,
  buildExecutableTrainingPrescription,
  buildScopedLearningHref,
  countClosedChoiceWrongMetrics,
  getLearningModuleLabel,
  type ExamCode,
  type LearnerNextStep,
  type TcfModuleId,
  type TrainingIntensity,
} from '@examflow/learning-space';
import { normalizeWritingAccuracyFromScore } from './openEndedWriting';
import type { TrainingAttemptEvidenceSource } from './trainingAttemptEvidence';

export type QuizResultMode = 'practice' | 'exam';

export type QuizReviewStatus = 'correct' | 'wrong' | 'unanswered' | 'graded';

export interface QuizAnswerRecord {
  questionId: string;
  answer: string | string[];
  isCorrect: boolean;
  timeSpent: number;
  sectionId?: string;
  responseMode?: 'closed' | 'open_ended';
  aiScore?: number;
  aiScoreMax?: number;
  aiGradeSource?: 'llm' | 'heuristic';
}

export interface QuizResultViewModelInput {
  mode: QuizResultMode;
  questions: Question[];
  answers: QuizAnswerRecord[];
  trainingContext?: QuizResultTrainingContext | null;
  examContext?: QuizResultExamContext | null;
}

export interface QuizResultStats {
  total: number;
  answered: number;
  correct: number;
  wrong: number;
  unanswered: number;
  accuracy: number;
}

export interface QuizWeakArea {
  key: string;
  label: string;
  issueCount: number;
  suggestion: string;
}

export interface QuizResultTrainingContext {
  contentPack: ExamCode;
  moduleId: TcfModuleId;
  moduleLabel: string;
  intensity: TrainingIntensity;
  intensityLabel: string;
  reportHref?: string | null;
  source?: TrainingAttemptEvidenceSource;
  languageId?: string | null;
}

export interface QuizResultExamContext {
  examType: string;
  examLabel: string;
}

export interface QuizResultLearningEvidence {
  title: string;
  body: string;
  prescriptionLabel: string;
  reportHref: string;
  nextTrainingHref: string;
}

export interface QuizResultDiagnosisReport {
  levelDisplay: string;
  levelDescription: string;
  cefr: string;
  accuracyPercent: number;
  /** Skill id for today's prescription — used when a weak area is tag-only. */
  prescriptionModuleId: string;
  weakAreas: Array<{
    label: string;
    sectionId?: string;
    incorrect: number;
    total: number;
    accuracyPercent: number;
  }>;
  todayTrainingTitle: string;
  todayTrainingReason: string;
  todayTrainingHref: string;
  reportHref: string;
}

export interface QuizResultMockExamEvidence {
  title: string;
  body: string;
  scopeLabel: string;
  reportHref: string;
}

export interface QuizResultRemediationPlan {
  title: string;
  body: string;
  reviewCount: number;
  focusLabel: string;
  reviewActionLabel: string;
  reviewHref: string;
  nextActionLabel: string;
  nextActionHref: string;
}

export interface QuizReviewRow {
  id: string;
  index: number;
  content: string;
  type: string;
  status: QuizReviewStatus;
  userAnswer: string;
  correctAnswer: string;
  explanation?: string;
  passage?: string;
  /** 材料轴含 audio 时，passage 展示为听力原文 */
  hasAudioStimulus: boolean;
}

export interface QuizResultViewModel {
  modeLabel: string;
  title: string;
  summaryText: string;
  primaryActionLabel: string;
  secondaryActionLabel: string;
  /** Drives secondary button icon — restart vs open diagnosis/prescription. */
  secondaryActionKind: 'restart' | 'rewrite' | 'prescription' | 'diagnosis';
  stats: QuizResultStats;
  weakAreas: QuizWeakArea[];
  remediationPlan: QuizResultRemediationPlan;
  nextStep: LearnerNextStep;
  learningEvidence: QuizResultLearningEvidence | null;
  diagnosisReport: QuizResultDiagnosisReport | null;
  mockExamEvidence: QuizResultMockExamEvidence | null;
  reviewRows: QuizReviewRow[];
}

export function buildQuizResultViewModel({
  mode,
  questions,
  answers,
  trainingContext = null,
  examContext = null,
}: QuizResultViewModelInput): QuizResultViewModel {
  const answerByQuestionId = new Map(answers.map((answer) => [answer.questionId, answer]));
  const total = questions.length;
  const answered = questions.filter((question) => answerByQuestionId.has(question.id)).length;
  const closedAnswers = answers.filter((answer) => answer.responseMode !== 'open_ended');
  const openEndedAnswers = answers.filter((answer) => answer.responseMode === 'open_ended');
  const writingOnly = openEndedAnswers.length > 0 && closedAnswers.length === 0;

  const correct = writingOnly
    ? 0
    : questions.filter((question) => {
        const answer = answerByQuestionId.get(question.id);
        return answer != null && answer.responseMode !== 'open_ended' && answer.isCorrect;
      }).length;
  const wrong = writingOnly
    ? 0
    : countClosedChoiceWrongMetrics(
        questions
          .map((question) => answerByQuestionId.get(question.id))
          .filter((answer): answer is QuizAnswerRecord => Boolean(answer))
      );
  const unanswered = Math.max(0, total - answered);
  const accuracy = writingOnly
    ? (() => {
        const abilityGrades = openEndedAnswers.filter(
          (answer) =>
            answer.aiGradeSource !== 'heuristic' && typeof answer.aiScore === 'number'
        );
        if (abilityGrades.length === 0) return 0;
        return Math.round(
          abilityGrades.reduce(
            (sum, answer) =>
              sum + normalizeWritingAccuracyFromScore(answer.aiScore, answer.aiScoreMax),
            0
          ) / abilityGrades.length
        );
      })()
    : total > 0
      ? Math.round((correct / total) * 100)
      : 0;

  const stats: QuizResultStats = {
    total,
    answered,
    correct,
    wrong,
    unanswered,
    accuracy,
  };

  const reviewRows = questions.map((question, index) => {
    const answer = answerByQuestionId.get(question.id);
    const status: QuizReviewStatus = !answer
      ? 'unanswered'
      : answer.responseMode === 'open_ended'
        ? 'graded'
        : answer.isCorrect
          ? 'correct'
          : 'wrong';

    return {
      id: question.id,
      index: index + 1,
      content: question.content,
      type: formatQuestionType(question.type),
      status,
      userAnswer: answer ? formatAnswer(answer.answer) : '未作答',
      correctAnswer:
        answer?.responseMode === 'open_ended'
          ? answer.aiScore != null
            ? `${answer.aiGradeSource === 'heuristic' ? '参考分' : 'AI'} ${answer.aiScore}/${answer.aiScoreMax ?? 20}`
            : '开放作答'
          : formatAnswer(question.correctAnswer),
      explanation: question.explanation,
      passage: question.passage,
      hasAudioStimulus: questionShowsAudioStimulus(question),
    };
  });

  const diagnosisReport = buildDiagnosisResultReport(trainingContext, questions, answers);
  const examCodeForLabels = trainingContext?.contentPack ?? examContext?.examType ?? null;
  const weakAreas = writingOnly
    ? []
    : diagnosisReport
      ? diagnosisReport.weakAreas.map((area) => ({
          key: area.sectionId || diagnosisReport.prescriptionModuleId || 'listening',
          label: area.label,
          issueCount: area.incorrect,
          suggestion: `优先巩固 ${area.label}（本次正确率 ${area.accuracyPercent}%）`,
        }))
      : buildWeakAreas(questions, answerByQuestionId, examCodeForLabels);
  const learningEvidence = buildLearningEvidence(
    trainingContext,
    stats,
    diagnosisReport,
    writingOnly
  );
  const mockExamEvidence = buildMockExamEvidence(mode, examContext, stats);
  const remediationPlan = writingOnly
    ? buildWritingRemediationPlan(stats, learningEvidence)
    : buildRemediationPlan({
        stats,
        weakAreas,
        reportHref:
          learningEvidence?.reportHref ??
          diagnosisReport?.reportHref ??
          mockExamEvidence?.reportHref ??
          null,
        nextTrainingHref:
          learningEvidence?.nextTrainingHref ?? diagnosisReport?.todayTrainingHref ?? null,
        examType: trainingContext?.contentPack ?? examContext?.examType ?? null,
        languageId:
          trainingContext?.languageId ??
          buildPlatformScope({
            examType: trainingContext?.contentPack ?? examContext?.examType ?? null,
          })?.languageId ??
          null,
        fallbackModuleId: trainingContext?.moduleId ?? null,
      });
  const nextStep = buildQuizResultNextStep(
    remediationPlan,
    learningEvidence,
    mockExamEvidence,
    diagnosisReport
  );

  const writingGrade = openEndedAnswers.find((answer) => typeof answer.aiScore === 'number');
  const writingSummary =
    writingGrade == null
      ? '本篇写作已完成。回训练入口可按官方 Task 再写一篇。'
      : writingGrade.aiGradeSource === 'heuristic'
        ? `本篇参考分 ${writingGrade.aiScore}/${writingGrade.aiScoreMax ?? 20}（不计能力主证据）。网络恢复后建议再评；也可回入口换 Task 再写。`
        : `本篇 AI ${writingGrade.aiScore}/${writingGrade.aiScoreMax ?? 20}（表现约 ${stats.accuracy}%）。建议针对弱项修订后，再按官方 Task 继续「写→评→改」。`;

  const secondaryActionKind = diagnosisReport
    ? ('diagnosis' as const)
    : writingOnly
      ? ('rewrite' as const)
      : remediationPlan.reviewCount > 0 || learningEvidence || mockExamEvidence
        ? ('prescription' as const)
        : ('restart' as const);
  const secondaryActionLabel =
    secondaryActionKind === 'diagnosis'
      ? '查看能力诊断'
      : secondaryActionKind === 'rewrite'
        ? '再写一篇'
        : secondaryActionKind === 'prescription'
          ? remediationPlan.reviewCount > 0
            ? remediationPlan.nextActionLabel
            : '查看能力处方'
          : '再练一组';

  return {
    modeLabel: diagnosisReport ? '入门诊断结果' : mode === 'exam' ? '模拟考试结果' : '练习结果',
    title: writingOnly
      ? '本篇写作已完成'
      : diagnosisReport
        ? '已建立能力基线'
        : getResultTitle(accuracy),
    summaryText: writingOnly
      ? writingSummary
      : diagnosisReport
        ? `本次诊断正确率 ${diagnosisReport.accuracyPercent}% · ${diagnosisReport.levelDisplay}`
        : getSummaryText(stats),
    primaryActionLabel: nextStep.action.label,
    secondaryActionLabel,
    secondaryActionKind,
    stats,
    weakAreas,
    remediationPlan,
    nextStep,
    learningEvidence,
    diagnosisReport,
    mockExamEvidence,
    reviewRows,
  };
}

function buildMockExamEvidence(
  mode: QuizResultMode,
  examContext: QuizResultExamContext | null,
  stats: QuizResultStats
): QuizResultMockExamEvidence | null {
  if (mode !== 'exam' || !examContext) {
    return null;
  }

  const platformScope = buildPlatformScope({ examType: examContext.examType });
  const scopeLabel = platformScope
    ? `${platformScope.languageName} · ${platformScope.examName}`
    : examContext.examLabel;

  return {
    title: '模考已进入能力诊断',
    body: `${examContext.examLabel} 模考完成，${stats.answered}/${stats.total} 题有效作答，正确率 ${stats.accuracy}%。能力报告会把这次模考作为 ${scopeLabel} 的阶段证据。`,
    scopeLabel,
    reportHref: buildMockExamReportHref(examContext),
  };
}

function buildRemediationPlan({
  stats,
  weakAreas,
  reportHref,
  nextTrainingHref,
  examType,
  languageId,
  fallbackModuleId,
}: {
  stats: QuizResultStats;
  weakAreas: QuizWeakArea[];
  reportHref: string | null;
  nextTrainingHref: string | null;
  examType: string | null;
  languageId: string | null;
  fallbackModuleId: TcfModuleId | null;
}): QuizResultRemediationPlan {
  const reviewCount = stats.wrong + stats.unanswered;
  const focusLabel = weakAreas[0]?.label ?? '本轮题目';
  const effectiveReportHref = reportHref ?? '/diagnosis/ability';

  if (reviewCount === 0) {
    return {
      title: '本轮无需错题复盘',
      body: '本轮没有错题或未答题，可以直接进入下一组限时训练或查看能力处方。',
      reviewCount,
      focusLabel,
      reviewActionLabel: '再练一组',
      reviewHref: nextTrainingHref ?? '/training',
      nextActionLabel: nextTrainingHref ? '进入下一次处方' : '再练一组',
      nextActionHref: nextTrainingHref ?? '/training',
    };
  }

  const skillId = resolveRemediationSkillId(weakAreas[0]?.key, fallbackModuleId);
  const remediationHref =
    examType && skillId
      ? buildAttemptHref({
          kind: 'remediation',
          languageId,
          examType: examType as ExamCode,
          skillId,
          intensity: 'short',
          reviewFocus: skillId,
          next: effectiveReportHref,
        })
      : effectiveReportHref;

  return {
    title: `先复盘${focusLabel}`,
    body: `${focusLabel} 有 ${reviewCount} 道错题或未答题。本页下方已有解析；下一步做一组同类短训，再更新能力处方。`,
    reviewCount,
    focusLabel,
    reviewActionLabel: skillId ? `重做${focusLabel}短训` : '查看能力处方',
    reviewHref: remediationHref,
    nextActionLabel: '查看能力处方',
    nextActionHref: effectiveReportHref,
  };
}

function resolveRemediationSkillId(
  focusKey: string | undefined,
  fallbackModuleId: TcfModuleId | null
): TcfModuleId | null {
  const normalized = String(focusKey || '')
    .trim()
    .toLowerCase();
  const modules: Record<string, TcfModuleId> = {
    listening: 'listening',
    reading: 'reading',
    writing: 'writing',
    speaking: 'speaking',
    language_structures: 'language_structures',
    tcf_listening: 'listening',
    tcf_reading: 'reading',
    tcf_language_structure: 'language_structures',
  };
  if (modules[normalized]) return modules[normalized];
  if (fallbackModuleId && modules[fallbackModuleId]) return fallbackModuleId;
  return null;
}

function buildQuizResultNextStep(
  remediationPlan: QuizResultRemediationPlan,
  learningEvidence: QuizResultLearningEvidence | null,
  mockExamEvidence: QuizResultMockExamEvidence | null,
  diagnosisReport: QuizResultDiagnosisReport | null
): LearnerNextStep {
  if (remediationPlan.reviewCount > 0) {
    return {
      id: 'review-result-errors',
      title: remediationPlan.title,
      description: remediationPlan.body,
      tone: 'warning',
      action: {
        id: 'review-result-errors',
        label: remediationPlan.reviewActionLabel,
        kind: 'primary',
        href: remediationPlan.reviewHref,
      },
    };
  }

  if (diagnosisReport) {
    return {
      id: 'continue-result-prescription',
      title: diagnosisReport.todayTrainingTitle,
      description: diagnosisReport.todayTrainingReason,
      tone: 'success',
      action: {
        id: 'start-diagnosis-prescription',
        label: '按诊断开始训练',
        kind: 'primary',
        href: diagnosisReport.todayTrainingHref,
      },
    };
  }

  return {
    id: 'continue-result-prescription',
    title: remediationPlan.nextActionLabel,
    description: learningEvidence?.body ?? mockExamEvidence?.body ?? remediationPlan.body,
    tone: learningEvidence || mockExamEvidence ? 'success' : 'current',
    action: {
      id: 'continue-result-prescription',
      label: remediationPlan.nextActionLabel,
      kind: 'primary',
      href: remediationPlan.nextActionHref,
    },
  };
}

function buildWritingRemediationPlan(
  stats: QuizResultStats,
  learningEvidence: QuizResultLearningEvidence | null
): QuizResultRemediationPlan {
  const nextHref = learningEvidence?.nextTrainingHref ?? '/training';
  return {
    title: '下一篇：按官方 Task 继续写',
    body: `本篇写作表现约 ${stats.accuracy}%。回入口选 Task（词数与建议用时不同），再走一遍「写→评→改」。`,
    reviewCount: 0,
    focusLabel: '写作任务',
    reviewActionLabel: '再写一篇',
    reviewHref: nextHref,
    nextActionLabel: '回写作入口',
    nextActionHref: nextHref,
  };
}

function buildLearningEvidence(
  trainingContext: QuizResultTrainingContext | null,
  stats: QuizResultStats,
  diagnosisReport: QuizResultDiagnosisReport | null,
  writingOnly = false
): QuizResultLearningEvidence | null {
  if (!trainingContext) {
    return null;
  }

  if (diagnosisReport) {
    return {
      title: '入门诊断已写入能力基线',
      body: `诊断等级 ${diagnosisReport.levelDisplay}。${diagnosisReport.todayTrainingReason}`,
      prescriptionLabel: diagnosisReport.todayTrainingTitle,
      reportHref: diagnosisReport.reportHref,
      nextTrainingHref: diagnosisReport.todayTrainingHref,
    };
  }

  if (trainingContext.source === 'diagnosis' || trainingContext.moduleId === 'diagnosis') {
    return null;
  }

  if (writingOnly || trainingContext.moduleId === 'writing') {
    const languageId =
      buildPlatformScope({ examType: trainingContext.contentPack })?.languageId ?? 'french';
    return {
      title: '写作证据已写入能力诊断',
      body: `本篇写作表现约 ${stats.accuracy}%。能力报告会更新写作建议；回入口可按官方 Task 再写一篇「写→评→改」。`,
      prescriptionLabel: `${trainingContext.moduleLabel} · 写→评→改`,
      reportHref: resolveTrainingReportHref(trainingContext),
      nextTrainingHref: buildScopedLearningHref(
        '/training',
        { languageId, examType: trainingContext.contentPack },
        { moduleId: 'writing', intensity: 'standard' }
      ),
    };
  }

  const prescription = buildExecutableTrainingPrescription({
    contentPack: trainingContext.contentPack,
    moduleId: trainingContext.moduleId,
    intensity: trainingContext.intensity,
  });

  return {
    title: '已写入能力诊断',
    body: `${trainingContext.moduleLabel} ${trainingContext.intensityLabel}完成，${stats.answered}/${stats.total} 题有效作答，正确率 ${stats.accuracy}%。能力报告会用这条证据更新下一次训练处方。`,
    prescriptionLabel: prescription.prescriptionLabel,
    reportHref: resolveTrainingReportHref(trainingContext),
    nextTrainingHref: prescription.sessionHref,
  };
}

function buildDiagnosisResultReport(
  trainingContext: QuizResultTrainingContext | null,
  questions: Question[],
  answers: QuizAnswerRecord[]
): QuizResultDiagnosisReport | null {
  if (!trainingContext || trainingContext.source !== 'diagnosis') {
    return null;
  }

  const report = buildQuickDiagnosisReport({
    examType: trainingContext.contentPack,
    languageId: trainingContext.languageId,
    questions,
    answers: answers.map((answer) => ({
      questionId: answer.questionId,
      isCorrect: answer.isCorrect,
      timeSpent: answer.timeSpent,
    })),
  });

  const prescriptionModuleId = report.todayTraining.sectionId ?? 'listening';
  const prescription = buildExecutableTrainingPrescription({
    contentPack: trainingContext.contentPack,
    moduleId: prescriptionModuleId,
    intensity: 'standard',
    reason: report.todayTraining.reason,
  });
  const reportHref = resolveTrainingReportHref({
    ...trainingContext,
    moduleId: prescriptionModuleId,
  });

  return {
    levelDisplay: report.level.display,
    levelDescription: report.level.description,
    cefr: report.level.cefr,
    accuracyPercent: Math.round(report.accuracy * 100),
    prescriptionModuleId,
    weakAreas: report.weakAreas.map((area) => ({
      label: area.label,
      sectionId: area.sectionId,
      incorrect: area.incorrect,
      total: area.total,
      accuracyPercent: Math.round(area.accuracy * 100),
    })),
    todayTrainingTitle: report.todayTraining.title,
    todayTrainingReason: report.todayTraining.reason,
    todayTrainingHref: prescription.sessionHref,
    reportHref,
  };
}

function resolveTrainingReportHref(trainingContext: QuizResultTrainingContext): string {
  if (
    trainingContext.reportHref &&
    trainingContext.reportHref.startsWith('/diagnosis/ability') &&
    !trainingContext.reportHref.startsWith('//')
  ) {
    return trainingContext.reportHref;
  }

  return buildAbilityReportHref(trainingContext);
}

function buildAbilityReportHref(trainingContext: QuizResultTrainingContext): string {
  const platformScope = buildPlatformScope({ examType: trainingContext.contentPack });
  const query = new URLSearchParams();

  if (platformScope?.languageId) query.set('languageId', platformScope.languageId);
  query.set('examType', trainingContext.contentPack);
  if (trainingContext.source !== 'diagnosis' && trainingContext.moduleId !== 'diagnosis') {
    query.set('section', trainingContext.moduleId);
  }
  query.set('source', trainingContext.source === 'diagnosis' ? 'diagnosis' : 'training-result');

  return `/diagnosis/ability?${query.toString()}`;
}

function buildMockExamReportHref(examContext: QuizResultExamContext): string {
  const platformScope = buildPlatformScope({ examType: examContext.examType });
  const query = new URLSearchParams();

  if (platformScope?.languageId) query.set('languageId', platformScope.languageId);
  query.set('examType', examContext.examType);
  query.set('source', 'mock-exam');

  return `/diagnosis/ability?${query.toString()}`;
}

function buildWeakAreas(
  questions: Question[],
  answerByQuestionId: Map<string, QuizAnswerRecord>,
  examCode: string | null = null
): QuizWeakArea[] {
  const areaMap = new Map<string, number>();

  questions.forEach((question) => {
    const answer = answerByQuestionId.get(question.id);
    if (answer?.responseMode === 'open_ended') return;
    const hasIssue = !answer || !answer.isCorrect;
    if (!hasIssue) return;

    const key = answer?.sectionId || question.section || question.type;
    areaMap.set(key, (areaMap.get(key) ?? 0) + 1);
  });

  return Array.from(areaMap.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([key, issueCount]) => ({
      key,
      label: toDisplayLabel(key, examCode),
      issueCount,
      suggestion: getWeakAreaSuggestion(key, issueCount, examCode),
    }));
}

function getResultTitle(accuracy: number) {
  if (accuracy >= 80) return '完成得很稳';
  if (accuracy >= 60) return '已经找到节奏';
  return '先抓住薄弱点';
}

function getSummaryText(stats: QuizResultStats) {
  if (stats.total === 0) return '本轮暂无可复盘题目，可以回到模块选择重新开始。';
  if (stats.accuracy >= 80) return '本轮正确率稳定，下一步可以提高题量或切换到限时训练。';
  if (stats.accuracy >= 60) return '本轮已经建立基本节奏，建议先复盘错题，再补一组同模块练习。';
  return '先不用追求题量，优先看错题和未答题，找出最容易丢分的题型。';
}

function getWeakAreaSuggestion(key: string, issueCount: number, examCode: string | null = null) {
  const label = toDisplayLabel(key, examCode);
  return `${label} 有 ${issueCount} 处需要复盘，建议先看解析再做一组同类题。`;
}

function formatQuestionType(type: string) {
  const label = getQuestionTypeLabel(type);
  if (label !== type) return label;
  return toDisplayLabel(type);
}

function toDisplayLabel(value: string, examCode: string | null = null) {
  const normalized = value.toLowerCase();
  const moduleAliases: Record<string, string> = {
    tcf_listening: 'listening',
    tcf_reading: 'reading',
    tcf_language_structure: 'language_structures',
  };
  const moduleId = moduleAliases[normalized] ?? normalized;
  if (examCode) {
    const labeled = getLearningModuleLabel(examCode as ExamCode, moduleId);
    if (labeled !== moduleId) {
      return labeled;
    }
  }

  return value
    .split(/[_-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatAnswer(answer: string | string[]) {
  return Array.isArray(answer) ? answer.join(', ') : answer;
}
